import {Tab, Tabs, Tooltip} from "@mui/material";
import {Link, Route, Routes, useLocation} from "react-router-dom";
import {map} from "lodash";
import './RoutingTabs.scss'
import {Colors} from "../styles/Colors";
import {useContext} from "react";
import {CompanyReactContext} from "../../contexts/CompanyContext";
import {UserReactContext} from "../../contexts/UserContext";


/**
 * A component that renders a set of routing tabs using Material UI Tabs and React Router.
 * It filters tabs based on feature flags and user roles (company admin).
 *
 * @param {object} params - The parameters object for the RoutingTabs component.
 * @param {Array<object>} params.tabs - An array of tab configuration objects.
 * @param {string} params.tabs[].label - The display label for the tab.
 * @param {string} params.tabs[].subPath - The sub-path for the tab, used in the URL and as the tab's value.
 * @param {React.ReactNode} params.tabs[].element - The React component to render when this tab is active.
 * @param {Array<string>} [params.tabs[].requiredFeatureFlags] - An optional array of feature flag names. The tab will only be shown if all specified feature flags are enabled for the company.
 * @param {boolean} [params.tabs[].showForCompanyAdminRegardlessOfFeatureFlags] - An optional flag. If true, the tab will be shown to company administrators regardless of the `requiredFeatureFlags`.
 * @param {boolean} [params.tabs[].requireCompanyAdmin] - An optional flag. If true, the tab will only be shown to company administrators, in addition to any `requiredFeatureFlags`.
 * @param {boolean} [params.tabs[].alwaysShow] - An optional flag. If true, the tab will always be shown but disabled if feature flags are not met.
 * @param {boolean} [params.tabs[].isDisabled] - An optional flag passed from parent. If true, the tab will be shown but disabled.
 * @param {string|React.ReactNode} [params.tabs[].disabledTooltip] - Optional tooltip content shown when the tab is disabled due to missing feature flags (useful with `alwaysShow`).
 * @param {string} params.baseRoute - The base route for the tabs, e.g., 'settings'.
 * @returns {JSX.Element} The rendered RoutingTabs component.
 */
export const RoutingTabs = ({tabs, baseRoute}) => {
    const companyContext = useContext(CompanyReactContext)
    const userContext = useContext(UserReactContext)
    // get the current tab from the url
    const location = useLocation();
    const currentTab = location.pathname.split(`${baseRoute}/`)[1] || tabs[0]?.subPath; // Changed to subPath as it's used for value

    // Nothing to render while the tab list is empty (e.g. still loading) —
    // also prevents a crash on a bare /<baseRoute> URL with no tabs
    if (tabs.length === 0) {
        return null;
    }

    // Process tabs to determine visibility and disabled state
    const processedTabs = tabs.map((tab) => {
        let hasAllFeatureFlags = true
        const isCompanyAdmin = userContext.isManagingCompany();

        if(tab.requiredFeatureFlags){
            hasAllFeatureFlags = tab.requiredFeatureFlags.every((featureFlag) => companyContext.featureFlags?.[featureFlag] || false)
        }

        // Determine if tab should be shown
        let shouldShow = false;

        // If alwaysShow is true, show the tab but potentially disable it
        if (tab.alwaysShow) {
            shouldShow = true;
        }
        // If this tab should show for company admins regardless of feature flags
        else if(tab.showForCompanyAdminRegardlessOfFeatureFlags && isCompanyAdmin){
            shouldShow = true;
        }
        // Check company admin requirement
        else if(tab.requireCompanyAdmin){
            shouldShow = hasAllFeatureFlags && isCompanyAdmin;
        }
        // Default: check feature flags
        else {
            shouldShow = hasAllFeatureFlags;
        }

        // Determine disabled state: either explicitly set or alwaysShow without feature flags
        const missingRequiredFF = !!tab.alwaysShow && !hasAllFeatureFlags;
        const isDisabled = tab.isDisabled || missingRequiredFF;

        return {
            ...tab,
            shouldShow,
            isDisabled,
            missingRequiredFF
        };
    });

    //filter tabs based on shouldShow
    const filteredTabs = processedTabs.filter((tab) => tab.shouldShow)
    return <div className="RoutingTabs">
        <Tabs value={currentTab}
              variant="scrollable"
              scrollButtons
              allowScrollButtonsMobile
        >
            {map(filteredTabs, (tab) => (
                <Tab
                    label={tab.missingRequiredFF && tab.disabledTooltip ? (
                        <Tooltip title={<span style={{ fontSize: 16 }}>{tab.disabledTooltip}</span>} arrow>
                            <span>{tab.label}</span>
                        </Tooltip>
                    ) : tab.label}
                    key={tab.subPath}
                    value={tab.subPath}
                    component={tab.isDisabled ? 'div' : Link}
                    to={tab.isDisabled ? undefined : `/${baseRoute}/${tab.subPath}`}
                    disabled={tab.isDisabled}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 'bold',
                        fontSize: '24px',
                        fontFamily: 'OpenSansCond',
                        '&.Mui-selected': {
                            color: Colors['$menuItemText'],
                        },
                        '&.Mui-disabled': {
                            opacity: 0.5,
                            cursor: 'not-allowed',
                            pointerEvents: 'auto', // allow tooltip hover on disabled tabs
                        },
                    }}
                />
            ))}
        </Tabs>
        <div className="routeContent">
            <Routes>
                {map(filteredTabs, (tab) => {
                    // Only render route if tab is not disabled
                    if (tab.isDisabled) {
                        return null;
                    }
                    return <Route path={tab.subPath} element={tab.element} key={tab.subPath}/>
                })}
            </Routes>
        </div>

    </div>
}